"use client";

import { useState, useRef, type FormEvent } from "react";
import { cn } from "@/lib/utils";

interface AICommandBarProps {
  onSubmit: (message: string) => void;
  loading?: boolean;
  isListening?: boolean;
  isVoiceSupported?: boolean;
  transcript?: string;
  interimTranscript?: string;
  onMicClick?: () => void;
  lastMessage?: string;
  suggestions?: string[];
  onSuggestionClick?: (suggestion: string) => void;
}

export function AICommandBar({
  onSubmit,
  loading,
  isListening,
  isVoiceSupported,
  transcript,
  interimTranscript,
  onMicClick,
  lastMessage,
  suggestions,
  onSuggestionClick,
}: AICommandBarProps) {
  const [input, setInput] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const message = input.trim();
    if (!message || loading) return;
    onSubmit(message);
    setInput("");
  };

  const displayText = isListening
    ? interimTranscript || transcript || "Listening..."
    : "";

  return (
    <div className="border-t border-white/8 bg-black/90 backdrop-blur-xl">
      {/* AI suggestion chips */}
      {suggestions && suggestions.length > 0 && (
        <div className="flex gap-2 px-4 pt-2 pb-1 overflow-x-auto">
          {suggestions.map((suggestion, i) => (
            <button
              key={i}
              onClick={() => onSuggestionClick?.(suggestion)}
              className="shrink-0 rounded-full border border-accent/20 bg-accent/5 px-3 py-1.5 text-xs text-accent hover:bg-accent/15 transition-colors"
            >
              {suggestion}
            </button>
          ))}
        </div>
      )}

      {/* Last AI message */}
      {lastMessage && !isListening && (
        <div className="px-4 pt-2 pb-1">
          <p className="text-xs text-accent/80 truncate">{lastMessage}</p>
        </div>
      )}

      {/* Input area */}
      <form onSubmit={handleSubmit} className="flex items-center gap-2 px-3 py-2">
        {/* Mic button */}
        {isVoiceSupported && (
          <button
            type="button"
            onClick={onMicClick}
            className={cn(
              "shrink-0 flex items-center justify-center rounded-full w-11 h-11 transition-all",
              isListening
                ? "bg-accent text-black animate-pulse-glow"
                : "bg-white/5 text-text-muted hover:bg-white/10 hover:text-white",
            )}
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 18.75a6 6 0 006-6v-1.5m-6 7.5a6 6 0 01-6-6v-1.5m6 7.5v3.75m-3.75 0h7.5M12 15.75a3 3 0 01-3-3V4.5a3 3 0 116 0v8.25a3 3 0 01-3 3z" />
            </svg>
          </button>
        )}

        {/* Text input */}
        <div className="flex-1 relative">
          <input
            ref={inputRef}
            type="text"
            value={isListening ? displayText : input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={isListening ? "Listening..." : "Type a command or tap mic..."}
            readOnly={isListening}
            className={cn(
              "w-full rounded-xl border bg-surface px-4 py-2.5 text-base text-white placeholder:text-text-dim",
              "focus:outline-none focus:ring-2 focus:ring-accent/30",
              isListening
                ? "border-accent/30 text-accent"
                : "border-surface-border",
            )}
          />
          {loading && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <svg className="h-4 w-4 animate-spin text-accent" viewBox="0 0 24 24" fill="none">
                <circle className="opacity-20" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-80" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
              </svg>
            </div>
          )}
        </div>

        {/* Send button */}
        <button
          type="submit"
          disabled={loading || isListening || !input.trim()}
          className={cn(
            "shrink-0 flex items-center justify-center rounded-xl w-11 h-11 transition-all",
            input.trim() && !loading
              ? "bg-white text-black hover:bg-white/90"
              : "bg-white/5 text-text-dim cursor-not-allowed",
          )}
        >
          <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
          </svg>
        </button>
      </form>
    </div>
  );
}
